// src/app/services/user.service.ts
import { effect, EffectRef, Injectable, signal } from '@angular/core';
import { onAuthStateChanged, Auth } from '@angular/fire/auth';
import { Usuario } from '../models/usuario.interface';
import { FirestoreService } from './firestore.service';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { QueryDocumentSnapshot, Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class UserService {
  private auth = inject(Auth);
  private firestore = inject(FirestoreService);
  private router = inject(Router);

  private _usuarioSignal = signal<Usuario | null>(null);
  public usuarioSignal = this._usuarioSignal.asReadonly();

  private unsubscribeUsuario: () => void = () => {};
  private unsubscribeEffectBaneo: EffectRef | null = null;
  private unsubscribeEffectExpiracion: EffectRef | null = null;

  constructor() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.inicializarUsuario(user.uid);
      } else {
        this.limpiarUsuario();
      }
    });
  }

  async getPerfil(uid: string): Promise<Usuario | null> {
    try {
      return await this.firestore.getDocById<Usuario>('usuarios', uid);
    } catch (error) {
      console.error(`[UserService] Error en getPerfil - usuarios/${uid}`, error);
      throw error;
    }
  }

  getPerfil$(uid: string): Observable<Usuario | null> {
    try {
      return this.firestore.listenDoc$<Usuario>('usuarios', uid);
    } catch (error) {
      console.error(`[UserService] Error en getPerfil$ - usuarios/${uid}`, error);
      throw error;
    }
  }

  async updatePerfil(uid: string, data: Partial<Usuario>): Promise<void> {
    try {
      return await this.firestore.updateDoc<Usuario>('usuarios', uid, data);
    } catch (error) {
      console.error(`[UserService] Error en updatePerfil - usuarios/${uid}`, error);
      throw error;
    }
  }

  inicializarUsuario(uid: string) {
    try {
      this.limpiarUsuario();

      this.unsubscribeUsuario = this.firestore.listenDoc<Usuario>('usuarios', uid, (data) => {
        if (data) {
          this._usuarioSignal.set({ ...data, uid });
        } else {
          this._usuarioSignal.set(null);
        }
      });

      this.unsubscribeEffectBaneo = effect(() => {
        const usuario = this._usuarioSignal();
        if (usuario && usuario.activo === false) {
          console.warn('[🛡️ UserService] Usuario baneado detectado, redirigiendo a /baneado');
          this.router.navigate(['/baneado'], { replaceUrl: true });
        }
      });

      this.unsubscribeEffectExpiracion = effect(() => {
        const usuario = this._usuarioSignal();
        if (!usuario || usuario.activo) return;

        if (usuario.baneadoHasta) {
          const fechaDesbaneo = usuario.baneadoHasta instanceof Timestamp
            ? usuario.baneadoHasta.toDate()
            : usuario.baneadoHasta;

          const now = new Date();
          if (fechaDesbaneo.getTime() <= now.getTime()) {
            console.log('[🛡️ UserService] Baneo expirado detectado, desbaneando automáticamente...');
            this.firestore.updateDoc<Usuario>('usuarios', usuario.uid, {
              activo: true,
              baneadoHasta: null
            }).then(() => {
              console.log('✅ Usuario desbaneado automáticamente.');
            }).catch(error => {
              console.error('[UserService] Error desbaneando automáticamente:', error);
            });
          }
        }
      });
    } catch (error) {
      console.error(`[UserService] Error en inicializarUsuario - usuarios/${uid}`, error);
    }
  }

  limpiarUsuario() {
    this.unsubscribeUsuario?.();
    this._usuarioSignal.set(null);
    this.unsubscribeUsuario = () => {};

    if (this.unsubscribeEffectBaneo) {
      this.unsubscribeEffectBaneo.destroy();
      this.unsubscribeEffectBaneo = null;
    }

    if (this.unsubscribeEffectExpiracion) {
      this.unsubscribeEffectExpiracion.destroy();
      this.unsubscribeEffectExpiracion = null;
    }
  }

  get usuarioActual(): Usuario | null {
    return this._usuarioSignal();
  }

  get uid(): string | null {
    return this._usuarioSignal()?.uid || null;
  }

  async obtenerUsuariosPaginados(
    pageSize = 20,
    lastSnapshot?: QueryDocumentSnapshot<Usuario>
  ): Promise<{
    usuarios: Usuario[];
    lastSnapshot: QueryDocumentSnapshot<Usuario> | null;
  }> {
    try {
      const result = await this.firestore.getPaginatedCollection<Usuario>(
        'usuarios',
        'creadoEn',
        pageSize,
        lastSnapshot
      );
      return {
        usuarios: result.data,
        lastSnapshot: result.lastSnapshot
      };
    } catch (error) {
      console.error('[UserService] Error en obtenerUsuariosPaginados - colección usuarios', error);
      throw error;
    }
  }

  async contarUsuarios(): Promise<number> {
    try {
      return await this.firestore.countCollection('usuarios');
    } catch (error) {
      console.error('[UserService] Error en contarUsuarios - colección usuarios', error);
      throw error;
    }
  }
}
